import React, { useState, useRef, useEffect } from "react";
import CodeIcon from "@mui/icons-material/Code";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TerminalIcon from "@mui/icons-material/Terminal";
import useJumboTheme from "@jumbo/hooks/useJumboTheme";
import compilerServices from "app/services/compiler-service";
import useMetadata from "app/hooks/useMetadata";
import codeConstants from "app/utils/constants/codeConstants";
import CodeEditorAndOutputPanel from "components/CodeEditorAndOutput/CodeEditorAndOutputPanel";
import EditorControls from "app/pages/ProblemDetail/CodeTestPanel/EditorControls";
import HistoryIcon from "@mui/icons-material/History";
import {
  LOADING_MESSAGES,
  SUBMISSION_STATUSES,
} from "app/utils/constants/constants";
import TestOutputContent from "./TestOutputContent";

const CodeExecutionPanel = ({
  problemData,
  code,
  setCode,
  selectedLanguage,
  setSelectedLanguage,
  editorLanguage,
  activeTemplates,
  availableLanguages,
  showSubmissionTab = false,
  submissionContent = null,
  onReset,
  onLanguageChange,
  onCodeSubmit,
  onEditorChange,
  onEditorMount,
  isActive,
  onActiveChange,
}) => {
  const { theme } = useJumboTheme();
  const {
    metadata,
    loading: metadataLoading,
    error: metadataError,
  } = useMetadata();
  const [isRunning, setIsRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [output, setOutput] = useState("");
  const [testResults, setTestResults] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [testCaseTabValue, setTestCaseTabValue] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("Compiling...");
  const [ioTabValue, setIOTabValue] = useState(0);
  const [codeTabValue, setCodeTabValue] = useState(0);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const pollingRef = useRef(null);

  useEffect(() => {
    if (!isPolling) return;
    const messages = LOADING_MESSAGES;
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setLoadingMessage(messages[index]);
    }, 2000);
    return () => clearInterval(interval);
  }, [isPolling]);

  const handleCodeRun = async () => {
    setIsRunning(true);
    setOutput("");
    setTestResults(null);
    const compilerRequest = {
      sourceCode: code,
      programmingLanguage: selectedLanguage.key,
      problemName: problemData?.name,
      executeParallely: true,
      finalSubmission: false,
      problemTests: problemData?.problemTests,
    };

    try {
      const response = await compilerServices.evaluateCode(compilerRequest);
      const { uniqueToken } = response;
      setIsPolling(true);
      pollRunStatus(uniqueToken);
    } catch (error) {
      console.error("Code evaluation error:", error);
      setOutput(`Error: ${error.message}`);
      setIsPolling(false);
      setIsRunning(false);
    }
  };

  const pollRunStatus = async (submissionId) => {
    const checkStatus = async () => {
      try {
        const result = await compilerServices.getSubmissionResultById(
          submissionId
        );
        setTestResults(result);
        if (
          result.status === SUBMISSION_STATUSES.PROCESSING ||
          result.status === SUBMISSION_STATUSES.IN_QUEUE
        ) {
          pollingRef.current = setTimeout(checkStatus, 1000);
        } else {
          setIsPolling(false);
          setIsRunning(false);
          setOutput("");
          clearTimeout(pollingRef.current);
        }
      } catch (error) {
        console.error("Polling error:", error);
        setOutput(`Polling Error: ${error.message}`);
        setIsPolling(false);
        setIsRunning(false);
        clearTimeout(pollingRef.current);
      }
    };
    checkStatus();
  };

  const handleCodeSubmitDefault = async () => {
    setIsSubmitting(true);
    setOutput("Submitting code...");
    setIsPolling(true);
    setTestResults(null);
    setIsSubmitClicked(true);
    setIOTabValue(1);
    setCodeTabValue(showSubmissionTab ? 1 : 0);

    const compilerRequest = {
      sourceCode: code,
      programmingLanguage: selectedLanguage.key,
      problemName: problemData?.name,
      executeParallely: true,
      finalSubmission: true,
      problemTests: showSubmissionTab ? [] : problemData?.problemTests,
    };

    try {
      const response = await compilerServices.evaluateCode(compilerRequest);
      const { uniqueToken } = response;
      pollSubmissionStatus(uniqueToken);
    } catch (error) {
      console.error("Code submission error:", error);
      setOutput(`Error: ${error.message}`);
      setIsPolling(false);
      setIsSubmitting(false);
    }
  };

  const pollSubmissionStatus = async (submissionId) => {
    const checkStatus = async () => {
      try {
        const result = await compilerServices.getSubmissionResultById(
          submissionId
        );
        setTestResults(result);
        if (
          result.status === SUBMISSION_STATUSES.PROCESSING ||
          result.status === SUBMISSION_STATUSES.IN_QUEUE
        ) {
          pollingRef.current = setTimeout(checkStatus, 1000);
        } else {
          setIsPolling(false);
          setIsSubmitting(false);
          setOutput("");
          clearTimeout(pollingRef.current);
        }
      } catch (error) {
        console.error("Polling error:", error);
        setOutput(`Polling Error: ${error.message}`);
        setIsPolling(false);
        setIsSubmitting(false);
        clearTimeout(pollingRef.current);
      }
    };
    checkStatus();
  };

  const handleResetDefault = () => {
    const template = activeTemplates.find(
      (t) => t.programmingLanguage === selectedLanguage.key
    )?.template;
    setCode(template || "");
    setOutput("");
    setTestResults(null);
    setIsSubmitClicked(false);
    setCodeTabValue(0);
  };

  const handleLanguageChangeDefault = (event) => {
    const lang = availableLanguages.find(
      (l) => l.key === event.target.value.key
    );
    setSelectedLanguage(lang);
    const template = activeTemplates.find(
      (t) => t.programmingLanguage === lang.key
    )?.template;
    setCode(template || "");
  };

  const codeTabs = [
    {
      icon: <CodeIcon />,
      label: showSubmissionTab ? "Code" : "Submission Code",
      content: null,
    },
    ...(showSubmissionTab && isSubmitClicked
      ? [
          {
            icon: <HistoryIcon />,
            label: "Submission",
            content: submissionContent,
          },
        ]
      : []),
  ];

  const outputTabs = [
    { icon: <AssignmentTurnedInIcon />, label: "Test Cases" },
    { icon: <TerminalIcon />, label: "Test Result" },
  ];

  const outputContent = TestOutputContent({
    problemData,
    testResults,
    isPolling,
    output,
    loadingMessage,
    testCaseTabValue,
    setTestCaseTabValue,
    theme,
    showOutputs: true,
    metadata,
  });

  const headerControls = (
    <EditorControls
      selectedLanguage={selectedLanguage}
      onLanguageChange={onLanguageChange || handleLanguageChangeDefault}
      onRun={handleCodeRun}
      onSubmit={onCodeSubmit || handleCodeSubmitDefault}
      onReset={onReset || handleResetDefault}
      isRunning={isRunning}
      isSubmitting={isSubmitting}
      hasActiveTemplates={activeTemplates.length > 0}
      metadataLoading={metadataLoading}
      metadataError={metadataError}
      availableLanguages={availableLanguages}
      codeConstants={codeConstants}
      isActive={isActive}
      onActiveChange={onActiveChange}
      setOutputTabValue={setIOTabValue}
      setCodeTabValue={0}
    />
  );

  return (
    <CodeEditorAndOutputPanel
      code={code}
      setCode={setCode}
      editorLanguage={editorLanguage}
      headerControls={headerControls}
      codeTabs={codeTabs}
      outputTabs={outputTabs}
      outputContent={outputContent}
      theme={theme}
      onEditorMount={onEditorMount}
      onChange={onEditorChange}
      codeTabValue={codeTabValue}
      setCodeTabValue={setCodeTabValue}
      ioTabValue={ioTabValue}
      setOutputTabValue={setIOTabValue}
      handleIOTabChange={(event, newValue) => setIOTabValue(newValue)}
    />
  );
};

export default CodeExecutionPanel;
